/**
 * useChatMessages - Hook de mensagens do chat.
 * Combina sincronização (useChatSync) e ações (useChatActions) para a conversa ativa.
 */
import { useRef } from 'react';
import { useChatSync } from './useChatSync';
import { useChatActions } from './useChatActions';
import { useOrganization } from '../../../contexts/OrganizationContext';
import type { ChatMessage, SendQuotedRef } from './types';

export function useChatMessages(
    instanceName: string | undefined,
    activeConversationId: string | null,
    connectionId?: string | null,
    chatPlatform: 'whatsapp' | 'instagram' = 'whatsapp',
    onSendFailure?: (
        error: unknown,
        text: string,
        quotedMessage?: SendQuotedRef
    ) => void,
    officialApi = false,
    onSendSucceeded?: () => void,
) {
    const { currentOrganization } = useOrganization();

    // 1. Hook de Sincronização (Leitura e Estado)
    const {
        messages,
        isLoading,
        conversationDbId,
        userId,
        refreshMessages,
        addOptimisticMessage,
        replaceOptimisticMessage,
        removeOptimisticMessage,
        patchLocalMessages,
        clearLocalOverride,
        setConversationDbId,
        // 🆕 Scroll Infinito
        loadOlderMessages,
        isLoadingOlder,
        hasMoreMessages,
        isPeerTyping,
        sendTyping,
    } = useChatSync(instanceName, activeConversationId, connectionId);

    const messagesRef = useRef<ChatMessage[]>(messages);
    messagesRef.current = messages;

    // 2. Hook de Ações (Escrita)
    const {
        sendMessage,
        sendMedia,
        sendContactCard,
        sendLocationMessage,
        sendPixMessage,
        deleteMessage,
        editMessage
    } = useChatActions({
        instanceName,
        activeConversationId,
        conversationDbId,
        chatPlatform,
        messagingConnectionId: connectionId,
        officialApi,
        userId,
        currentOrganizationId: currentOrganization?.id,
        addOptimisticMessage,
        replaceOptimisticMessage,
        removeOptimisticMessage,
        patchLocalMessages,
        clearLocalOverride,
        getLocalMessage: (id) => messagesRef.current.find(m => m.id === id),
        setConversationDbId,
        onSendFailure,
        onSendSucceeded,
    });

    return {
        messages,
        isLoading,
        sendMessage,
        sendMedia,
        sendContactCard,
        sendLocationMessage,
        sendPixMessage,
        deleteMessage,
        editMessage,
        refreshMessages,
        // 🆕 Scroll Infinito
        loadOlderMessages,
        isLoadingOlder,
        hasMoreMessages,
        isPeerTyping,
        sendTyping,
    };
}
